import { apiSlice } from './apiSlice';
import {
  ChatAppRequest,
  ChatAppResponse,
  Config,
  SimpleAPIResponse,
  HistoryListApiResponse,
  HistoryApiResponse,
} from './models';

export const chatApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    config: builder.query<Config, { customer_id: string }>({
      queryFn: async () => {
        const data: Config = {
          showChatHistoryBrowser: false,
          showChatHistoryCosmos: true,
          showGPT4VOptions: false,
          showLanguagePicker: false,
          showSemanticRankerOption: true,
          useSemanticRankerOption: false,
          showSpeechInput: true,
          showSpeechOutputAzure: false,
          showSpeechOutputBrowser: true,
          showUserUpload: false,
          showVectorOption: true,
          showQueryRewritingOption: true,
        };
        return { data };
      },
      providesTags: ['Config'],
    }),

    ask: builder.mutation<ChatAppResponse, ChatAppRequest>({
      query: (body) => ({
        url: '/ask',
        method: 'POST',
        body,
      }),
    }),

    chat: builder.mutation<Response, { request: ChatAppRequest; shouldStream: boolean; customer_id: string }>({
      query: ({ request, shouldStream, customer_id }) => ({
        url: shouldStream ? `/api/chat/${customer_id}/stream` : `/api/chat/${customer_id}`,
        method: 'POST',
        body: request,
        responseHandler: async (response) => response,
      }),
      invalidatesTags: ['TokenUsage'],
    }),

    getSpeech: builder.mutation<string | null, { text: string }>({
      query: ({ text }) => ({
        url: '/speech',
        method: 'POST',
        body: { text },
        responseHandler: async (response) => await response.blob(),
        cache: 'no-cache',
      }),
      transformResponse: (blob: Blob, meta: { response?: Response } = {}) => {
        const status = meta.response?.status;
        if (status === 200) return URL.createObjectURL(blob);
        if (status === 400) return null;
        return null;
      },
    }),

    uploadFile: builder.mutation<SimpleAPIResponse, FormData>({
      query: (formData) => ({
        url: '/upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['UploadedFiles'],
    }),

    deleteUploadedFile: builder.mutation<SimpleAPIResponse, { filename: string }>({
      query: ({ filename }) => ({
        url: '/delete_uploaded',
        method: 'POST',
        body: { filename },
      }),
      invalidatesTags: ['UploadedFiles'],
    }),

    listUploadedFiles: builder.query<string[], void>({
      query: () => ({
        url: '/list_uploaded',
        method: 'GET',
      }),
      providesTags: ['UploadedFiles'],
    }),

    postChatHistory: builder.mutation<any, any>({
      query: (item) => ({
        url: '/api/chat_history',
        method: 'POST',
        body: item,
      }),
      invalidatesTags: ['ChatHistories'],
    }),

    getChatHistoryList: builder.query<HistoryListApiResponse, { count: number; continuationToken?: string }>({
      query: ({ count, continuationToken }) => {
        let url = `/api/chat_history/sessions?count=${count}`;
        if (continuationToken) {
          url += `&continuation_token=${encodeURIComponent(continuationToken)}`;
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['ChatHistories'],
    }),

    getChatHistory: builder.query<HistoryApiResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/api/chat_history/sessions/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [{ type: 'ChatHistory', id }],
    }),

    deleteChatHistory: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/api/chat_history/sessions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'ChatHistory', id }, 'ChatHistories'],
    }),

    getCitationFile: builder.query<string, { customerId: string; fileName: string }>({
      query: ({ customerId, fileName }) => ({
        url: `/api/content/${customerId}/${fileName}`,
        method: 'GET',
        responseHandler: async (response) => URL.createObjectURL(await response.blob()),
        cache: 'no-cache',
      }),
    }),

    getChatHistoryListByReference: builder.query<
      HistoryListApiResponse,
      { referenceId: string; count: number; continuationToken?: string }
    >({
      query: ({ referenceId, count, continuationToken }) => {
        let url = `/api/chat_history/sessions/reference/${encodeURIComponent(referenceId)}?count=${count}`;
        if (continuationToken) {
          url += `&continuation_token=${encodeURIComponent(continuationToken)}`;
        }
        return { url, method: 'GET' };
      },
      providesTags: (result, error, { referenceId }) => [{ type: 'ChatHistories', id: referenceId }],
    }),
    getSessionMessages: builder.query<HistoryApiResponse, { id: string; entraOid: string; referenceId: string }>({
      query: ({ id, entraOid, referenceId }) => ({
        url:
          `/api/chat_history/sessions/${id}/messages?entra_oid=${encodeURIComponent(entraOid)}` +
          `&reference_id=${encodeURIComponent(referenceId)}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useConfigQuery,
  useAskMutation,
  useChatMutation,
  useGetSpeechMutation,
  useUploadFileMutation,
  useDeleteUploadedFileMutation,
  useListUploadedFilesQuery,
  usePostChatHistoryMutation,
  useGetChatHistoryListQuery,
  useGetChatHistoryQuery,
  useLazyGetChatHistoryQuery,
  useDeleteChatHistoryMutation,
  useGetCitationFileQuery,
  useLazyGetCitationFileQuery,
  useLazyGetChatHistoryListQuery,
  useGetChatHistoryListByReferenceQuery,
  useLazyGetChatHistoryListByReferenceQuery,
  useGetSessionMessagesQuery,
  useLazyGetSessionMessagesQuery,
} = chatApiSlice;
